import clsx from 'clsx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { DismissButton } from '../dismiss-button';
import { InteractableComponent } from '../interactable-component';
import { Text } from '../text';

export const DEFAULT_COUNTDOWN_TIMEOUT_MS = 5000;
const TICK_INTERVAL_MS = 50;

export type CountdownProps = {
  timeoutMs?: number;
  title: string;
  label?: string;
  onClick?: () => void;
  onCancel?: () => void;
  onBlur?: () => void;
  onTimeout?: () => void;
  className?: string;
};

export function Countdown({
  timeoutMs = DEFAULT_COUNTDOWN_TIMEOUT_MS,
  title,
  label,
  onClick,
  onCancel,
  onBlur,
  onTimeout,
  className,
}: CountdownProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const hasSettledRef = useRef(false);
  const onClickRef = useRef(onClick);
  const onCancelRef = useRef(onCancel);
  const onBlurRef = useRef(onBlur);
  const onTimeoutRef = useRef(onTimeout);
  const [remainingMs, setRemainingMs] = useState(timeoutMs);

  onClickRef.current = onClick;
  onCancelRef.current = onCancel;
  onBlurRef.current = onBlur;
  onTimeoutRef.current = onTimeout;

  const settle = useCallback((callback?: () => void) => {
    if (hasSettledRef.current) {
      return;
    }
    hasSettledRef.current = true;
    callback?.();
  }, []);

  useEffect(() => {
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      setRemainingMs(Math.max(0, timeoutMs - (Date.now() - startedAt)));
    }, TICK_INTERVAL_MS);
    const timeoutId = window.setTimeout(() => {
      settle(onTimeoutRef.current);
    }, timeoutMs);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [settle, timeoutMs]);

  useEffect(() => {
    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node) || rootRef.current?.contains(target)) {
        return;
      }
      settle(onBlurRef.current);
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [settle]);

  const remainingSeconds = Math.max(1, Math.ceil(remainingMs / 1000));
  const progressPercent =
    timeoutMs <= 0 ? 0 : Math.min(100, (remainingMs / timeoutMs) * 100);

  return (
    <div
      ref={rootRef}
      aria-label={title}
      aria-live="polite"
      className={clsx(
        'bg-brand-orange-50 relative flex items-center gap-2 overflow-hidden rounded-full py-1.5 pl-3 pr-1',
        className
      )}
      role="status"
    >
      <InteractableComponent
        className="flex min-w-0 flex-1 cursor-pointer items-center rounded-full px-0.5 outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2"
        onPress={() => settle(onClickRef.current)}
        role="button"
        tabIndex={0}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <Text type="body-sm" className="text-dark min-w-0 truncate">
            {title}
          </Text>
          <Text type="body-xs" className="shrink-0 text-gray-400">
            {label ?? `${remainingSeconds}s`}
          </Text>
        </div>
      </InteractableComponent>
      <DismissButton
        onPress={() => settle(onCancelRef.current)}
        size="xs"
        tooltip="Cancel"
      />
      <div className="bg-brand-orange-100 absolute inset-x-0 bottom-0 h-0.5">
        <div
          className="bg-brand-orange-500 h-full"
          style={{ width: `${progressPercent}%` }}
        />
      </div>
    </div>
  );
}
